import React, { useEffect } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Typography, DialogTitle, DialogContent, Stack, Box, Grid, useTheme, Avatar } from "@mui/material";
import imageSats from "assets/images/sats.png";
import { Credit } from "components/icons";
import { fontWeight } from "config/Theme/options";
import { useMUIThemeModeContext } from "context/MUIThemeModeContext";
import { useAPICashbackSatsUtilizations } from "hooks/useAPI";
import { useSnackbar } from "notistack";
import { Trans, useTranslation } from "react-i18next";
import { CashbackSatsUtilizationType } from "types/enums";
import { ICashbackSatsUtilization } from "types/types";
import { getColorByMode } from "utils";
import { renderDateTime, renderNumber, renderPrice } from "utils/formatter";

import { StyledDialog, StyledIconButton } from "../UtilizationsModal/styles";
import { StyledTransactionBox } from "./styles";
import IProps from "./types";

const TransactionItem = ({ createdAt, czkValue, type, btcCzkRate, credits, sats }: ICashbackSatsUtilization) => {
  const { t } = useTranslation();
  const { colors } = useMUIThemeModeContext();

  return (
    <StyledTransactionBox>
      <Grid container spacing={1} alignItems="flex-start">
        <Grid item xs={8} md={9}>
          <Grid container spacing={0} rowSpacing={0.5}>
            <Grid item xs={12} md={4} lg={4}>
              <Stack spacing={0.75}>
                <Typography
                  color={colors.gray500}
                  fontWeight={fontWeight.medium}
                  fontSize={{ xs: "0.75rem", sm: "0.875rem" }}>
                  {renderDateTime(createdAt)}
                </Typography>
                <Typography
                  color={colors.black}
                  fontWeight={fontWeight.medium}
                  display={{ xs: "block", md: "none" }}
                  fontSize={{ xs: "0.75rem", sm: "0.875rem" }}>
                  {t(`cashbackSatsList.${type}`)}
                </Typography>
                <Typography color={colors.black} fontSize={{ xs: "0.75rem", md: "0.875rem" }}>
                  <span style={{ color: colors.gray500, fontSize: "0.75rem", fontWeight: fontWeight.regular }}>
                    {t("cashbackSatsList.historyModal.value")}:
                  </span>
                  {" "}
                  <span style={{ color: colors.black, fontWeight: fontWeight.medium }}>
                    {renderPrice(czkValue, undefined, 2, true)}
                  </span>
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} md={8} lg={8}>
              <Stack spacing={{ xs: 0, md: 0.75 }}>
                <Typography
                  color={colors.black}
                  fontWeight={fontWeight.medium}
                  display={{ xs: "none", md: "block" }}
                  fontSize={{ xs: "0.75rem", sm: "0.875rem" }}>
                  {t(`cashbackSatsList.${type}`)}
                </Typography>
                <Typography color={colors.black} fontSize={{ xs: "0.75rem", md: "0.875rem" }}>
                  <span style={{ color: colors.gray500, fontSize: "0.75rem", fontWeight: fontWeight.regular }}>
                    {t("cashbackSatsList.historyModal.btcPrice")}:
                  </span>
                  {" "}
                  <span style={{ color: colors.black, fontWeight: fontWeight.medium }}>{renderPrice(btcCzkRate)}</span>
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={4} md={3}>
          <Stack alignItems="flex-end" spacing={0.5}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography
                color={colors.darkBlue}
                fontSize={{ xs: "0.75rem", md: "0.875rem" }}
                fontWeight={fontWeight.semiBold}>
                {`-${renderNumber(sats ?? 0)}`}
              </Typography>
              <Avatar src={imageSats} sx={{ width: "16px", height: "16px" }} />
            </Box>
            {credits !== null && type !== CashbackSatsUtilizationType.DONATION_TO_STOSUJ && (
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <Typography
                  color={colors.success}
                  fontSize={{ xs: "0.75rem", md: "0.875rem" }}
                  fontWeight={fontWeight.medium}>
                  {`+${renderNumber(credits ?? 0)}`}
                </Typography>
                <Credit sx={{ fontSize: 18 }} />
              </Box>
            )}
          </Stack>
        </Grid>
      </Grid>
    </StyledTransactionBox>
  );
};

const HistoryModal: React.FC<IProps> = ({ utilized, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const theme = useTheme();
  const { colors } = useMUIThemeModeContext();
  const { data, error } = useAPICashbackSatsUtilizations();

  useEffect(() => {
    if (error) {
      onClose();
      enqueueSnackbar(t("commonError"), { variant: "error" });
    }
  }, [error]);

  return (
    <StyledDialog open={true} onClose={onClose} fullWidth scroll="paper">
      <StyledIconButton color="inherit" onClick={onClose} size="small">
        <CloseIcon />
      </StyledIconButton>
      <DialogTitle>{t("cashbackSatsList.historyModal.title")}</DialogTitle>
      <DialogContent>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1, md: 4 }} pb={{ xs: 2, md: 3 }}>
          <Stack>
            <Typography
              color={getColorByMode(theme, colors.gray500, colors.gray600)}
              fontWeight={fontWeight.regular}
              fontSize={{ xs: "0.625rem", sm: "0.75rem" }}>
              {t("cashbackSatsList.historyModal.utilized")}
            </Typography>
            <Typography color={colors.black} fontWeight={fontWeight.medium} fontSize={{ xs: "1.25rem", sm: "1.5rem" }}>
              {renderNumber(utilized)}&nbsp;{t(`cashbackSatsList.sats`, { count: utilized })}
            </Typography>
          </Stack>
          <Typography
            color={colors.blueGray}
            fontWeight={fontWeight.light}
            fontSize={{ xs: "0.75rem", sm: "0.875rem" }}>
            <Trans i18nKey="cashbackSatsList.historyModal.description" />
          </Typography>
        </Stack>
        <Stack spacing={1.5} pb={3}>
          {data?.cashbackSatsUtilizations?.map((item, idx) => (
            <TransactionItem key={idx} {...item} />
          ))}
        </Stack>
      </DialogContent>
    </StyledDialog>
  );
};

export default HistoryModal;
